import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { TaggedList } from '@client/components/_ui/display/TaggedList';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { productVersionFormStore } from '@client/components/products-v2/store';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { CONTACT_FIELD_TYPE_LABELS } from '@shared/constants/business/product';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute(
    '/console/$orgId/configuration/claims-products/products-v2/details/$productId/product-versions/details/$productVersionId/contact-types',
)({
    staticData: {
        ui: {
            useParentTitle: false,
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ContactTypes />
        </PageController>
    );
}

type QueryParams = RouterInputs['productVersionContactType']['listProductVersionContactTypes'];

const columnHelper = createColumnHelper<
    RouterOutputs['productVersionContactType']['listProductVersionContactTypes']['rows'][number],
    QueryParams['orderBy']
>();

function ContactTypes() {
    const orgId = useOrgId();
    const isReadOnly = productVersionFormStore.use.isReadonly();
    const { productId, productVersionId } = Route.useParams();
    const { trpcUtils } = useTrpc();
    const { mutate } = trpc.productVersionContactType.deleteProductVersionContactType.useMutation();
    const navigate = useNavigate();

    const [input, setInput] = useState<QueryParams>({
        where: {
            productVersionId,
        },
    });

    const { data, isPending, error } = trpc.productVersionContactType.listProductVersionContactTypes.useQuery({
        ...input,
        where: {
            ...input.where,
            productVersionId,
        },
    });

    const tableProps = usePrismaTable({
        rowKey: 'id',
        data: data?.rows,
        total: data?.total,
        loading: isPending,
        queryParams: input,
        onParamsChange(params) {
            setInput({
                ...params,
                where: {
                    ...params.where,
                    productVersionId,
                },
            });
        },
        pagination: {
            position: 'none',
        },
        columns: [
            columnHelper.accessor('name', {
                id: 'name',
                header: 'Name',
                sortable: 'name',
            }),
            columnHelper.accessor('ProductVersionContactTypeFields', {
                id: 'Fields',
                header: 'Fields',
                cell({ row }) {
                    return (
                        <TaggedList
                            items={row.ProductVersionContactTypeFields}
                            accessor={(item) => CONTACT_FIELD_TYPE_LABELS[item.type]}
                            emptyText="No fields"
                        />
                    );
                },
            }),
            ...(isReadOnly
                ? []
                : [
                      columnHelper.actions({
                          cell({ row }) {
                              return (
                                  <Button.Delete
                                      onConfirm={(onSuccess) =>
                                          mutate(
                                              { id: row.id },
                                              {
                                                  onSuccess() {
                                                      onSuccess();
                                                      void trpcUtils.productVersionContactType.invalidate();
                                                  },
                                                  onError(error) {
                                                      errorMessage.show(error);
                                                  },
                                              },
                                          )
                                      }
                                  />
                              );
                          },
                      }),
                  ]),
        ],
    });

    return (
        <TRPCErrorBoundary error={error}>
            <Outlet />
            <TableToolbar
                actions={
                    !isReadOnly && (
                        <Button.Add.Link
                            to="/console/$orgId/configuration/claims-products/products-v2/details/$productId/product-versions/details/$productVersionId/contact-types/add"
                            params={{
                                orgId,
                                productId,
                                productVersionId,
                            }}
                            variant="primary"
                            label="New contact type"
                        />
                    )
                }
            />
            <PrismaTable
                {...tableProps}
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick(e) {
                        navigate(
                            {
                                to: '/console/$orgId/configuration/claims-products/products-v2/details/$productId/product-versions/details/$productVersionId/contact-types/edit/$contactTypeId',
                                params: {
                                    orgId,
                                    productId,
                                    productVersionId,
                                    contactTypeId: id,
                                },
                            },
                            e,
                        );
                    },
                })}
            />
        </TRPCErrorBoundary>
    );
}
